import React from 'react';
import Selectors from 'src/utils/selectors';
import adobeTags from 'src/types/adobe-tags';
import styles from 'src/components/atomic/molecules/spendingLineGraphChart/spendingLineGraphChart.module.scss';
import { DlsTypographyVariants } from 'src/types/dls-typography-variants.enum';
import Circle from 'src/components/atomic/atoms/circle/Circle';
import LiteralsService from 'src/services/literals/LiteralsService';
import Highchart from 'src/components/atomic/atoms/highchart/Highchart';
import { useSpendingHistory } from 'src/hooks/queries/spending';
import { BUDGET_OVERALL_ID } from 'src/services/budget.service';
import { useSyncingAccounts } from 'src/hooks/useSyncingAccounts';
import { getChartOptions } from 'src/components/atomic/molecules/spendingLineGraph/chartConfig';
import { useBudget } from 'src/hooks/queries/budgets';
import Loading from 'src/components/atomic/atoms/loading/Loading';
import { SpendingLineGraphChartOption } from 'src/types/spendingLineGraph';
import DateUtils from 'src/utils/date';
import { SpendingTimePeriod } from 'src/types/spending';
import TotalAverageInfoModal from 'src/components/atomic/organisms/totalAverageInfoModal/TotalAverageInfoModal';

interface Props {
    title: string;
    comparisonMonthOption: SpendingLineGraphChartOption;
}
export default function SpendingLineGraphChart({ title, comparisonMonthOption }: Props): JSX.Element {
    const { isLoading: isLoadingMonthlyBudget, data: monthlyBudget } = useBudget(BUDGET_OVERALL_ID);
    const { isLoading: isLoadingComparisonMonthSpendingHistory, data: comparisonMonthSpendingHistory } =
        useSpendingHistory({
            id: BUDGET_OVERALL_ID,
            timePeriod: SpendingTimePeriod.DAY,
            startDate: comparisonMonthOption.startDate,
            endDate: comparisonMonthOption.endDate,
            cumulative: true
        });

    const { isLoading: isLoadingCurrentMonthSpendingHistory, data: currentMonthSpendingHistory } = useSpendingHistory({
        id: BUDGET_OVERALL_ID,
        timePeriod: SpendingTimePeriod.DAY,
        endDate: DateUtils.daysAhead(1),
        cumulative: true
    });

    const { isSyncingAccounts } = useSyncingAccounts();

    const isLoadingChart: boolean =
        isLoadingMonthlyBudget ||
        isLoadingComparisonMonthSpendingHistory ||
        isLoadingCurrentMonthSpendingHistory ||
        isSyncingAccounts;

    const hasChartData: boolean =
        comparisonMonthSpendingHistory?.[0]?.averageSpent > 0 || currentMonthSpendingHistory?.[0]?.averageSpent > 0;

    const chartOptions = hasChartData
        ? getChartOptions(currentMonthSpendingHistory, comparisonMonthSpendingHistory, monthlyBudget?.monthlyAmount)
        : getChartOptions([], [], 0);

    if (isLoadingChart) return <Loading lg />;

    return (
        <Highchart
            title={title}
            options={chartOptions}
            data-cy={Selectors.SpendSmart.SPENDING_LINE_GRAPH}
            tagName={adobeTags.CurrentMonth.LineGraph}
        >
            <div className={styles.chartTopRow}>
                <TotalAverageInfoModal
                    anchorTitle={LiteralsService.getLiteral('TotalAverageInfoModal.anchorTitle1')}
                    anchorTag={adobeTags.TotalAverageInfoModal.OpenSpendingTotal}
                />
                <div className={styles.legend}>
                    <div className={`${styles.legendItem} ${DlsTypographyVariants.BODY_1}`}>
                        <Circle diameter="10px" className={styles.currentMonth} />
                        {LiteralsService.getLiteral('Budget.Current.graph.currentMonth')}
                    </div>
                    <div className={`${styles.legendItem} ${DlsTypographyVariants.BODY_1}`}>
                        <Circle diameter="10px" className={styles.lastMonth} />
                        {comparisonMonthOption.label}
                    </div>
                </div>
            </div>
        </Highchart>
    );
}
